/**
 * The Pulumi programs this release changes, and what was done about each.
 *
 * This card exists because the component table cannot answer for them. Every row
 * there ends in a Deploy button that runs a module's own `pnpm run deploy`, and no
 * module's deploy script runs `pulumi up` — so a release that changes `ml/infra` or
 * `automation/deployer/infra` ships the code and leaves the projects, service
 * accounts, IAM bindings and custom roles it describes unapplied, with every row on
 * the page still reading succeeded. Before this card the page's silence was the
 * whole problem: nothing was wrong, and nothing said so.
 *
 * There is no Apply button, and there will not be one. Running a program creates
 * exactly the authority an attacker would want the release host to have, which is
 * why its service account does not have it (THREAT_MODEL §12). So the card's job
 * ends at reporting, and the only thing an operator can press is the record of what
 * they did elsewhere — the `pulumi up` they ran in the program directory, or the
 * reading that this change needed none.
 *
 * Which makes acknowledging here a different act from acknowledging a component
 * skip. A skip says "production is knowingly without this code"; this says "the
 * infrastructure is there, I checked". Nothing in the system can verify it, since
 * the host cannot read the stack — so the reason is the only evidence, and the close
 * has no override that skips past it.
 */
import { useState } from 'react';
import {
    Alert,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    INSTRUCTION_KIND,
    legalInstructions,
    treeUrl,
    type Acknowledgement,
    type InfraChange,
    type InstructionKind,
    type Phase,
    type ReleaseDoc,
} from '@finvari/deployer';
import { agoTextCoarse } from '../lib/hostStatus';
import { createInstruction } from '../lib/createInstruction';
import { dbForRole } from '../firebase';
import { useNow, CAPTION_TICK_MS } from '../hooks';
import { useOperator } from '../auth';

export function InfraChangesCard({ release, phase }: { release: ReleaseDoc; phase: Phase }) {
    const changes = release.infraChanges ?? [];

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="baseline">
                        <Typography variant="h6">Infrastructure</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {summaryText(release)}
                        </Typography>
                    </Stack>

                    {changes.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
                            No Pulumi program changed between the baseline and the release, so there is nothing for an
                            owner to apply. Said rather than left blank: when a release <em>does</em> change one,
                            nothing else on this page would tell you, and an empty card here is what makes that
                            silence trustworthy.
                        </Typography>
                    ) : (
                        <>
                            <Alert severity="warning">
                                Deploying a component does not apply these. An owner runs <code>pulumi up</code> in the
                                program directory — this host has no authority to create the projects, service accounts
                                and IAM bindings a program describes, and no way to read the stack to see whether
                                anyone has. The release cannot close until each one below has a reason on the record,
                                and there is no override for it.
                            </Alert>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Program</TableCell>
                                        <TableCell>Pulumi project</TableCell>
                                        <TableCell>Files</TableCell>
                                        <TableCell>State</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {changes.map((change) => (
                                        <InfraRow
                                            key={change.program}
                                            change={change}
                                            acknowledgement={release.infraAcknowledgements?.[change.program]}
                                            version={release.version}
                                            releaseTag={release.releaseTag}
                                            phase={phase}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

/** The heading caption: how many programs, and how many are still unaccounted for. */
function summaryText(release: ReleaseDoc): string {
    const changes = release.infraChanges ?? [];
    if (changes.length === 0) return 'No Pulumi program changed in this release.';
    const acknowledged = changes.filter((c) => release.infraAcknowledgements?.[c.program]).length;
    const programs = `${changes.length} Pulumi program${changes.length === 1 ? '' : 's'} changed`;
    return acknowledged === changes.length
        ? `${programs} · all accounted for`
        : `${programs} · ${changes.length - acknowledged} still to account for`;
}

function InfraRow({
    change,
    acknowledgement,
    version,
    releaseTag,
    phase,
}: {
    change: InfraChange;
    acknowledgement: Acknowledgement | undefined;
    version: string;
    releaseTag: string;
    phase: Phase;
}) {
    const operator = useOperator();
    const now = useNow(CAPTION_TICK_MS);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const run = async (kind: InstructionKind, params: Record<string, unknown>) => {
        setSending(true);
        setError(undefined);
        const result = await createInstruction(dbForRole('release'), 'release', kind, params, operator, version);
        setSending(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        setDialogOpen(false);
        setReason('');
    };

    const acknowledgeReason = reasonFor(phase, INSTRUCTION_KIND.deployAcknowledgeInfra);
    const cancelReason = reasonFor(phase, INSTRUCTION_KIND.deployCancelInfraAcknowledgement);

    return (
        <>
            <TableRow hover>
                <TableCell>
                    {/*
                      * To the program directory at the release's own tag, not at main:
                      * an operator opening this months later is asking what this
                      * release changed, and main has moved on.
                      */}
                    <Link href={treeUrl(releaseTag, change.program)} target="_blank" rel="noreferrer">
                        <code>{change.program}</code>
                    </Link>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" color="text.secondary">
                        {change.name}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Tooltip title={fileTooltip(change)}>
                        <Typography variant="body2">{change.fileCount}</Typography>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    {acknowledgement ? (
                        <Stack spacing={0.25}>
                            <Chip size="small" color="success" label="accounted for" />
                            <Tooltip title={acknowledgementTooltip(acknowledgement, now)}>
                                {/* Clipped by CSS rather than a slice, so the cut lands
                                    on the pixel and the table column stays narrow. */}
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    sx={{ display: 'block', maxWidth: 280 }}
                                >
                                    {acknowledgement.reason}
                                </Typography>
                            </Tooltip>
                        </Stack>
                    ) : (
                        <Chip size="small" color="warning" label="needs an owner to apply it" />
                    )}
                </TableCell>
                <TableCell align="right">
                    {acknowledgement ? (
                        <Tooltip
                            title={
                                cancelReason ??
                                `Removes the reason, and ${change.program} holds the release open again`
                            }
                        >
                            <span>
                                {/*
                                  * No dialog: it undoes a record the operator just made,
                                  * the reason is on screen beside the button, and making
                                  * it again costs one dialog.
                                  */}
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    disabled={!!cancelReason || sending}
                                    onClick={() =>
                                        void run(INSTRUCTION_KIND.deployCancelInfraAcknowledgement, {
                                            program: change.program,
                                        })
                                    }
                                >
                                    Cancel
                                </Button>
                            </span>
                        </Tooltip>
                    ) : (
                        <Tooltip title={acknowledgeReason ?? 'Record what was done about this program'}>
                            <span>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="warning"
                                    disabled={!!acknowledgeReason || sending}
                                    onClick={() => setDialogOpen(true)}
                                >
                                    Acknowledge
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </TableCell>
            </TableRow>
            {error && !dialogOpen && (
                <TableRow>
                    <TableCell colSpan={5}>
                        <Alert severity="error">{error}</Alert>
                    </TableCell>
                </TableRow>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography variant="h6">What was done about {change.program}?</Typography>
                        <DialogContentText>
                            {version} changes {change.fileCount} file{change.fileCount === 1 ? '' : 's'} in this
                            program and nothing in the release applies them. Either an owner has run{' '}
                            <code>pulumi up</code> in <code>{change.program}</code>, or the change does not alter what
                            the stack provisions — say which, because nothing here can check it and this reason is the
                            only evidence there will be.
                        </DialogContentText>
                        <TextField
                            label="What was done, and by whom"
                            placeholder="pulumi up run against the prod stack by …, preview was empty afterwards"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                            autoFocus
                        />
                        {error && <Alert severity="error">{error}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        disabled={sending || reason.trim().length === 0}
                        onClick={() =>
                            void run(INSTRUCTION_KIND.deployAcknowledgeInfra, {
                                program: change.program,
                                reason: reason.trim(),
                            })
                        }
                    >
                        Acknowledge
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

/** The files, for the count that stands in for them. Capped on the host side. */
function fileTooltip(change: InfraChange): string {
    const listed = change.files.join('\n');
    return change.files.length < change.fileCount ? `${listed}\n… and ${change.fileCount - change.files.length} more` : listed;
}

/** The whole reason, and who owns the decision, for the caption that shows part of it. */
function acknowledgementTooltip(ack: Acknowledgement, now: number): string {
    const at = ack.at?.toMillis?.();
    const when = at === undefined ? '' : ` ${agoTextCoarse(Math.max(0, now - at))}`;
    return `Acknowledged by ${ack.by.email}${when}: ${ack.reason}`;
}

/** The operator-facing reason a release action is unavailable in this phase, if any. */
function reasonFor(phase: Phase, kind: InstructionKind): string | undefined {
    return legalInstructions(phase, 'release').find((l) => l.kind === kind)?.reasonIfIllegal;
}
